<template>
		<v-app>
			<a class="skip-link" href="#main-content">
				{{ locale === 'de' ? 'Zum Inhalt springen' : 'Skip to content' }}
			</a>
			<AppHeader
				:is-admin-route="isAdminRoute"
			:is-header-hidden="isHeaderHidden"
			:is-past-hero="isPastHero"
			:logo-url="logoUrl"
			:md-and-up="mdAndUp"
			:is-legal-route="isLegalRoute"
			:active-section="activeSection"
			:locale-options="localeOptions"
			:locale="locale"
			:drawer="drawer"
			@update:drawer="drawer = $event"
			@nav="nav"
			@go-home="goHome"
			@on-locale-change="onLocaleChange"
		/>

		<v-main id="main-content" class="app-main" tabindex="-1">
			<!-- Rechtsseiten -->
			<RouterView v-if="isNonLandingRoute" />

			<!-- One-Pager Content -->
			<template v-else>
				<HeroSection
					:section-id="ids.home"
					:md-and-up="mdAndUp"
					:komp-video="kompVideo"
					:komp-mobile-video="kompMobileVideo"
					:motion-paused="motionPaused"
					:is-trust-marquee-in-view="isTrustMarqueeInView"
					:trusted-brands="trustedBrands"
					:locale="locale"
					@nav="nav"
					@toggle-motion="toggleMotionPlayback"
				/>

				<ProblemsSection
					:section-id="ids.problems"
					@nav="nav"
				/>

				<ApproachSection
					:l-hero-img="lHeroImg"
					:l-hero-img2x="lHeroImg2x"
					:locale="locale"
					:approach-steps="approachSteps"
					:active-approach-step="activeApproachStep"
					:prev-approach-step-index="prevApproachStepIndex"
					:next-approach-step-index="nextApproachStepIndex"
					:approach-node-track-class="approachNodeTrackClass"
					@next-step="nextApproachStep"
					@prev-step="prevApproachStep"
					@set-active="activeApproachStep = $event"
				/>

					<StoriesSection
						:section-id="ids.stories"
					:story-stations="storyStations"
					:active-story-station="activeStoryStation"
					:is-stories-spy-visible="isStoriesSpyVisible"
					:komp-one-video="kompOneVideo"
					:komp-two-video="kompTwoVideo"
					:komp-three-video="kompThreeVideo"
					:komp-four-video="kompFourVideo"
					:subwebpage-video="subwebpageVideo"
					:bestellung-end-to-end-img="bestellungEndToEndImg"
					:automatische-provisionsabrechnungen-img="automatischeProvisionsabrechnungenImg"
					@scroll-to-station="scrollToStoryStation"
					@focus-station-index="focusStorySpyByIndex"
					@focus-station-offset="focusStorySpyOffset($event.id, $event.offset)"
				/>

				<!-- BLOG -->
				<HomeBlogSection :section-id="ids.blog" />


				<AboutSection :section-id="ids.wer" @nav="nav" />

				<DoingSection :section-id="ids.was" />


				<CoreSection :section-id="ids.core" />

				<ComplianceSection :section-id="ids.compliance" :deco-img="iHeroImg" />

				<FactsSection :section-id="ids.facts" />

				<CareerSection :section-id="ids.career" :locale="locale" @nav="nav" />

				<ContactSection :section-id="ids.contact" :faq-section-id="ids.faq" :logo-url="logoUrl" :deco-img="qHeroImg" />

			</template>

			<AppFooter
				v-if="!isAdminRoute"
				:logo-url="logoUrl"
				:locale="locale"
				:year="year"
				@nav="nav"
				@open-consent="openConsent"
			/>
		</v-main>

		<v-btn
			v-if="!isAdminRoute"
			icon
			size="large"
			class="scroll-top-btn"
			:class="{ 'is-visible': showScrollTop }"
			:aria-label="locale === 'en' ? 'Scroll to top' : 'Nach oben scrollen'"
			@click="scrollToTop"
		>
			<v-icon>mdi-arrow-up</v-icon>
		</v-btn>
	</v-app>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount, watch, defineAsyncComponent, defineComponent, h } from 'vue'
import { useDisplay } from 'vuetify'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AppHeader from './components/layout/AppHeader.vue'
import AppFooter from './components/layout/AppFooter.vue'
import HeroSection from './components/sections/HeroSection.vue'
import ProblemsSection from './components/sections/ProblemsSection.vue'
import { openCookieConsentPreferences } from './lib/cookie-consent'
import { SITE_URL } from './lib/runtime-config'
import logoUrl from './assets/LinqForce_Ill.svg'
import kompVideo from './assets/Komp.webm'
import kompMobileVideo from './assets/Komp_mobile.webm'
import kompOneVideo from './assets/Komp_1.webm'
import kompTwoVideo from './assets/Komp_2.webm'
import kompThreeVideo from './assets/Komp_3.webm'
import kompFourVideo from './assets/Komp_4.webm'
import subwebpageVideo from './assets/Subwebpage.webm'
import lHeroImg from './assets/L-Hero_435.webp'
import lHeroImg2x from './assets/L-Hero_870.webp'
import iHeroImg from './assets/I-Hero.webp'
import qHeroImg from './assets/Q-Hero.webp'
import haraLogo from './assets/hara.webp'
import hylaLogo from './assets/hyla.webp'
import koenigswegeLogo from './assets/koenigswege.webp'
import sanusLogo from './assets/Sanus.webp'
import aegLogo from './assets/aeg.webp'
import automatischeProvisionsabrechnungenImg from './assets/automatische_provisionsabrechnungen.webp'
import bestellungEndToEndImg from './assets/bestellungendtoend.webp'

const HomeBlogLoading = defineComponent({
	name: 'HomeBlogLoading',
	render() {
		return h('div', { class: 'home-blog-loading', role: 'status', 'aria-live': 'polite' }, 'Loading blog section...')
	},
})

const HomeBlogError = defineComponent({
	name: 'HomeBlogError',
	render() {
		return h(
			'div',
			{ class: 'home-blog-error', role: 'alert' },
			'Blog section could not be loaded. Please reload the page.'
		)
	},
})

const HomeBlogSection = defineAsyncComponent({
	loader: () => import('./components/HomeBlogSection.vue'),
	loadingComponent: HomeBlogLoading,
	errorComponent: HomeBlogError,
	delay: 120,
})
const ApproachSection = defineAsyncComponent(() => import('./components/sections/ApproachSection.vue'))
const StoriesSection = defineAsyncComponent(() => import('./components/sections/StoriesSection.vue'))
const AboutSection = defineAsyncComponent(() => import('./components/sections/AboutSection.vue'))
const DoingSection = defineAsyncComponent(() => import('./components/sections/DoingSection.vue'))
const CoreSection = defineAsyncComponent(() => import('./components/sections/CoreSection.vue'))
const ComplianceSection = defineAsyncComponent(() => import('./components/sections/ComplianceSection.vue'))
const FactsSection = defineAsyncComponent(() => import('./components/sections/FactsSection.vue'))
const CareerSection = defineAsyncComponent(() => import('./components/sections/CareerSection.vue'))
const ContactSection = defineAsyncComponent(() => import('./components/sections/ContactSection.vue'))

const { t, locale } = useI18n()
function setLocale(next: 'de' | 'en') {
	locale.value = next
	localStorage.setItem('lf-locale', next)
}
function onLocaleChange(value: string) {
	if (value === 'de' || value === 'en') {
		setLocale(value)
	}
}
const localeOptions = [
	{ title: 'DE', value: 'de' },
	{ title: 'EN', value: 'en' },
] as const

const drawer = ref(false)
const { mdAndUp } = useDisplay()
const year = new Date().getFullYear()

const route = useRoute()
const router = useRouter()
const isLegalRoute = computed(() =>
	['/impressum', '/datenschutz', '/barrierefreiheit', '/dsar/verify'].includes(route.path)
	|| route.path.startsWith('/blog/')
	|| route.name === 'NotFound'
)
const isAdminRoute = computed(() => route.path.startsWith('/admin'))
const isNonLandingRoute = computed(() => route.path !== '/')

const ids = {
	home: 'home', problems: 'problems', pricing: 'pricing', stories: 'stories', caseStudies: 'case-studies', blog: 'blog', wer: 'wer', was: 'was', core: 'core',
	subwebpages: 'subwebpages', interfaces: 'interfaces', compliance: 'compliance', facts: 'facts', career: 'career', contact: 'contact', faq: 'faq'
} as const

const trustedBrands = [
	{ name: 'hara', logo: haraLogo },
	{ name: 'hyla', logo: hylaLogo },
	{ name: 'koenigswege', logo: koenigswegeLogo },
	{ name: 'sanus', logo: sanusLogo },
	{ name: 'aeg', logo: aegLogo },
] as const

const approachSteps = computed(() => ([
	{
		id: 'analyze',
		title: t('approach.steps.analyze.title'),
		icon: 'mdi-magnify',
		text: t('approach.steps.analyze.text'),
	},
	{
		id: 'design',
		title: t('approach.steps.design.title'),
		icon: 'mdi-vector-polyline',
		text: t('approach.steps.design.text'),
	},
	{
		id: 'connect',
		title: t('approach.steps.connect.title'),
		icon: 'mdi-connection',
		text: t('approach.steps.connect.text'),
	},
	{
		id: 'deploy',
		title: t('approach.steps.deploy.title'),
		icon: 'mdi-rocket-launch-outline',
		text: t('approach.steps.deploy.text'),
	},
	{
		id: 'optimize',
		title: t('approach.steps.optimize.title'),
		icon: 'mdi-chart-timeline-variant',
		text: t('approach.steps.optimize.text'),
	},
]))
const hasCompletedFirstApproachLoop = ref(false)
const activeApproachStep = ref(0)
const prevApproachStepIndex = computed(() => (
	approachSteps.value.length > 1 && !(!hasCompletedFirstApproachLoop.value && activeApproachStep.value === 0)
		? (activeApproachStep.value - 1 + approachSteps.value.length) % approachSteps.value.length
		: null
))
const nextApproachStepIndex = computed(() => (
	approachSteps.value.length > 1
		? (activeApproachStep.value + 1) % approachSteps.value.length
		: null
))
const approachNodeTrackClass = computed(() => `approach-node-track--${activeApproachStep.value}`)
const storyStations = computed(() => ([
	{ id: 'story-station-leads-quick', label: t('stories.spy.leads') },
	{ id: 'story-station-otc-quick', label: t('stories.spy.otcImpulse') },
	{ id: 'story-station-team', label: t('stories.spy.team') },
	{ id: 'story-station-subwebpages', label: t('stories.spy.subwebpages') },
	{ id: 'story-station-provision', label: t('stories.spy.provision') },
	{ id: 'story-station-subscription', label: t('stories.spy.subscription') },
]))
const activeStoryStation = ref<(typeof storyStations.value)[number]['id']>('story-station-leads-quick')
const isStoriesSpyVisible = ref(false)
const prefersReducedMotion = ref(false)
const motionPaused = ref(false)
const isTrustMarqueeInView = ref(true)
const isStoriesInView = ref(false)
const motionEffectsEnabled = computed(() => mdAndUp.value && !prefersReducedMotion.value)

function nextApproachStep() {
	const steps = approachSteps.value
	if (steps.length < 2) return
	const atEnd = activeApproachStep.value >= steps.length - 1
	if (atEnd) {
		hasCompletedFirstApproachLoop.value = true
		activeApproachStep.value = 0
		return
	}
	activeApproachStep.value += 1
}

function prevApproachStep() {
	const steps = approachSteps.value
	if (steps.length < 2) return
	activeApproachStep.value = (activeApproachStep.value - 1 + steps.length) % steps.length
}

function updateStoriesScrollSpy() {
	const candidates = storyStations.value
		.map((station) => {
			const el = storyStationElements.get(station.id)
			if (!el) return null
			const rect = el.getBoundingClientRect()
			const distance = Math.abs(rect.top - (window.innerHeight * STORIES_SPY_ANCHOR_VIEWPORT_RATIO))
			return { id: station.id, distance }
		})
		.filter((entry): entry is { id: (typeof storyStations.value)[number]['id']; distance: number } => Boolean(entry))

	if (!candidates.length) return
	candidates.sort((a, b) => a.distance - b.distance)
	activeStoryStation.value = candidates[0].id
}

function updateStoriesSpyVisibility() {
	if (!motionEffectsEnabled.value) {
		isStoriesSpyVisible.value = false
		return
	}

	const storiesRoot = storiesSectionEl.value
	const first = storyStationElements.get(storyStations.value[0].id)
	const last = storyStationElements.get(storyStations.value[storyStations.value.length - 1].id)
	if (!storiesRoot || !first || !last) {
		isStoriesSpyVisible.value = false
		return
	}

	const rootRect = storiesRoot.getBoundingClientRect()
	const sectionInRange = rootRect.top <= STORIES_SPY_TRIGGER_TOP_PX && rootRect.bottom > STORIES_SPY_TRIGGER_TOP_PX + STORIES_SPY_LAST_OFFSET_PX
	if (!sectionInRange) {
		isStoriesSpyVisible.value = false
		return
	}

	const firstTopReached = first.getBoundingClientRect().top <= STORIES_SPY_TRIGGER_TOP_PX
	const lastPassed = last.getBoundingClientRect().bottom <= STORIES_SPY_TRIGGER_TOP_PX + STORIES_SPY_LAST_OFFSET_PX
	isStoriesSpyVisible.value = firstTopReached && !lastPassed
}

function scrollToStoryStation(id: (typeof storyStations.value)[number]['id']) {
	document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

function focusStorySpyByIndex(index: number) {
	const next = storyStations.value[index]
	if (!next) return
	scrollToStoryStation(next.id)
	requestAnimationFrame(() => {
		const buttons = Array.from(document.querySelectorAll<HTMLButtonElement>('.stories-spy-item'))
		buttons[index]?.focus()
	})
}

function focusStorySpyOffset(currentId: (typeof storyStations.value)[number]['id'], offset: 1 | -1) {
	const index = storyStations.value.findIndex((station) => station.id === currentId)
	if (index < 0) return
	const nextIndex = Math.max(0, Math.min(storyStations.value.length - 1, index + offset))
	focusStorySpyByIndex(nextIndex)
}

type HeaderSection = 'home' | 'problems' | 'blog' | 'core' | 'career' | 'contact'

const visibleRatios = new Map<string, number>()
const sectionNavGroupById: Record<string, HeaderSection> = {
  [ids.home]: 'home',
  [ids.problems]: 'problems',
  [ids.pricing]: 'problems',
  [ids.stories]: 'problems',
  [ids.caseStudies]: 'problems',
  [ids.blog]: 'blog',
  [ids.wer]: 'core',
  [ids.was]: 'core',
  [ids.core]: 'core',
  [ids.compliance]: 'core',
  [ids.facts]: 'core',
  [ids.career]: 'career',
  [ids.contact]: 'contact',
  [ids.faq]: 'contact',
}

// Auto-Highlight: aktive Navigationsgruppe
const activeSection = ref<HeaderSection>('home')
const isHeaderHidden = ref(false)
const isPastHero = ref(false)
const showScrollTop = ref(false)
let lastScrollY = 0

function go(anchor: keyof typeof ids) {
	const targetId = anchor === 'core' ? ids.wer : ids[anchor]
	document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function hashForAnchor(anchor: keyof typeof ids) {
	const targetId = anchor === 'core' ? ids.wer : ids[anchor]
	return `#${targetId}`
}

function upsertMeta(query: string, attr: 'name' | 'property', value: string) {
	let el = document.head.querySelector(`meta[${attr}="${query}"]`) as HTMLMetaElement | null
	if (!el) {
		el = document.createElement('meta')
		el.setAttribute(attr, query)
		document.head.appendChild(el)
	}
	el.setAttribute('content', value)
}

function upsertLink(rel: string, href: string, hreflang?: string) {
	const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`
	let el = document.head.querySelector(selector) as HTMLLinkElement | null
	if (!el) {
		el = document.createElement('link')
		el.setAttribute('rel', rel)
		if (hreflang) el.setAttribute('hreflang', hreflang)
		document.head.appendChild(el)
	}
	el.setAttribute('href', href)
}

function upsertJsonLd(id: string, payload: Record<string, unknown> | null) {
	const selector = `script[type="application/ld+json"][data-seo-id="${id}"]`
	const existing = document.head.querySelector(selector)
	if (!payload) {
		existing?.remove()
		return
	}
	let el = existing as HTMLScriptElement | null
	if (!el) {
		el = document.createElement('script')
		el.setAttribute('type', 'application/ld+json')
		el.setAttribute('data-seo-id', id)
		document.head.appendChild(el)
	}
	el.textContent = JSON.stringify(payload)
}

function localeHref(path: string, targetLocale: 'de' | 'en') {
	return targetLocale === 'en' ? `${SITE_URL}${path}?lang=en` : `${SITE_URL}${path}`
}

function applySeoMeta() {
	const routeMeta = route.meta as { seoTitleKey?: string; seoDescriptionKey?: string } | undefined
	let title = routeMeta?.seoTitleKey ? t(routeMeta.seoTitleKey) : t('seo.homeTitle')
	let description = routeMeta?.seoDescriptionKey ? t(routeMeta.seoDescriptionKey) : t('seo.homeDescription')
	if (route.name === 'CaseStudy') {
		const slugParam = Array.isArray(route.params.slug) ? route.params.slug[0] : route.params.slug
		const slug = typeof slugParam === 'string' ? slugParam : ''
		const itemName = t(`caseStudies.items.${slug}.name`)
		const itemStory = t(`caseStudies.items.${slug}.story`)
		if (itemName && itemName !== `caseStudies.items.${slug}.name`) {
			title = `${itemName} – ${t('seo.pages.caseStudyTitle')}`
		}
		if (itemStory && itemStory !== `caseStudies.items.${slug}.story`) {
			description = itemStory
		}
	}
	const canonicalUrl = localeHref(route.path, locale.value === 'en' ? 'en' : 'de')

	document.title = title
	document.documentElement.lang = locale.value

	upsertMeta('description', 'name', description)
	upsertMeta('robots', 'name', 'index,follow')
	upsertMeta('og:site_name', 'property', 'LINQFORCE')
	upsertMeta('og:type', 'property', 'website')
	upsertMeta('og:title', 'property', title)
	upsertMeta('og:description', 'property', description)
	upsertMeta('og:url', 'property', canonicalUrl)
	upsertMeta('og:locale', 'property', locale.value === 'de' ? 'de_DE' : 'en_US')
	upsertMeta('og:image', 'property', `${SITE_URL}/social-card.png`)
	upsertMeta('og:image:width', 'property', '1200')
	upsertMeta('og:image:height', 'property', '630')
	upsertMeta('twitter:card', 'name', 'summary_large_image')
	upsertMeta('twitter:title', 'name', title)
	upsertMeta('twitter:description', 'name', description)
	upsertMeta('twitter:image', 'name', `${SITE_URL}/social-card.png`)

	upsertLink('canonical', canonicalUrl)
	upsertLink('alternate', `${SITE_URL}${route.path}`, 'x-default')
	upsertLink('alternate', localeHref(route.path, 'de'), 'de')
	upsertLink('alternate', localeHref(route.path, 'en'), 'en')

	upsertJsonLd('organization', {
		'@context': 'https://schema.org',
		'@type': 'Organization',
		name: 'Linqforce GmbH',
		url: SITE_URL,
		email: 'info@linqforce.de',
		telephone: '+49 521 55730910',
		address: {
			'@type': 'PostalAddress',
			streetAddress: 'Herforder Str. 237',
			postalCode: '33609',
			addressLocality: 'Bielefeld',
			addressCountry: 'DE',
		},
	})
	upsertJsonLd('website', {
		'@context': 'https://schema.org',
		'@type': 'WebSite',
		name: 'LINQFORCE',
		url: SITE_URL,
		inLanguage: locale.value === 'en' ? 'en' : 'de',
	})
	if (route.path === '/') {
		upsertJsonLd('faq', {
			'@context': 'https://schema.org',
			'@type': 'FAQPage',
			mainEntity: ['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight'].map((key) => ({
				'@type': 'Question',
				name: t(`faq.items.${key}.title`),
				acceptedAnswer: {
					'@type': 'Answer',
					text: t(`faq.items.${key}.text`),
				},
			})),
		})
	} else {
		upsertJsonLd('faq', null)
	}
	if (route.path.startsWith('/blog/') && route.name !== 'BlogDetail') {
		upsertJsonLd('article', {
			'@context': 'https://schema.org',
			'@type': 'Article',
			headline: title,
			description,
			inLanguage: locale.value === 'en' ? 'en' : 'de',
			mainEntityOfPage: canonicalUrl,
			author: { '@type': 'Organization', name: 'Linqforce GmbH' },
			publisher: { '@type': 'Organization', name: 'Linqforce GmbH' },
			image: `${SITE_URL}/social-card.png`,
		})
	} else if (route.name !== 'BlogDetail') {
		upsertJsonLd('article', null)
	}
}
async function nav(anchor: keyof typeof ids) {
	const hash = hashForAnchor(anchor)
	if (route.path !== '/') {
		await router.push({ path: '/', hash })
		await nextTick()
		setTimeout(() => go(anchor), 0)
	} else {
		await router.replace({ path: '/', hash })
		go(anchor)
	}
	drawer.value = false
}

function goHome() {
	if (route.path !== '/' || route.hash) {
		router.push({ path: '/', hash: '' })
	}
}

// Cookie-Settings (ESM & CDN kompatibel)
function openConsent() {
	openCookieConsentPreferences()
}

function runWhenIdle(callback: () => void, timeout = 1200) {
	const idleWindow = window as Window & {
		requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number
	}

	if (idleWindow.requestIdleCallback) {
		idleWindow.requestIdleCallback(callback, { timeout })
		return
	}

	window.setTimeout(callback, timeout)
}

function syncMotionMediaPlayback() {
	const videos = Array.from(document.querySelectorAll<HTMLVideoElement>('.hero-bg-video, .hero-mobile-video, .story-visual-video'))
	videos.forEach((video) => {
		video.muted = true
		video.defaultMuted = true
		video.playsInline = true
		if (motionPaused.value) {
			video.pause()
			return
		}
		void video.play().catch(() => {})
	})
}

function toggleMotionPlayback() {
	motionPaused.value = !motionPaused.value
	syncMotionMediaPlayback()
}

function runScrollWork() {
	scrollTicking = false
	if (document.visibilityState !== 'visible') return
	const currentY = window.scrollY || 0
	showScrollTop.value = currentY > 440
	if (isNonLandingRoute.value) return
	const heroEl = heroSectionEl.value
	const heroBottom = heroEl ? heroEl.offsetTop + heroEl.offsetHeight : 0
	isPastHero.value = currentY > Math.max(heroBottom - HERO_HEADER_OFFSET_PX, HERO_HEADER_OFFSET_PX)

	if (motionEffectsEnabled.value) {
		if (isStoriesInView.value) {
			const now = performance.now()
			if (now - lastStoriesMetricsAt >= STORIES_METRICS_INTERVAL_MS) {
				updateStoriesScrollSpy()
				updateStoriesSpyVisibility()
				lastStoriesMetricsAt = now
			}
		} else {
			lastStoriesMetricsAt = 0
			isStoriesSpyVisible.value = false
		}
	} else {
		isStoriesSpyVisible.value = false
	}

	if (currentY <= HEADER_TOP_RESET_PX) {
		isHeaderHidden.value = false
		activeSection.value = 'home'
		lastScrollY = currentY
		return
	}

	const delta = currentY - lastScrollY
	if (delta > HEADER_SCROLL_DELTA_TOGGLE_PX) isHeaderHidden.value = true
	else if (delta < -HEADER_SCROLL_DELTA_TOGGLE_PX) isHeaderHidden.value = false

	updateActiveSectionByPosition()

	lastScrollY = currentY
}

function scrollToTop() {
	window.scrollTo({ top: 0, behavior: 'smooth' })
}

function updateActiveSectionByPosition() {
	if (isNonLandingRoute.value) return
	const anchorOffset = 120
	const points = (Object.entries(ids) as Array<[keyof typeof ids, string]>)
		.map(([key, id]) => {
			const el = document.getElementById(id)
			if (!el) return null
			const topDistance = Math.abs(el.getBoundingClientRect().top - anchorOffset)
			return { key, topDistance }
		})
		.filter((entry): entry is { key: keyof typeof ids; topDistance: number } => Boolean(entry))
		.sort((a, b) => a.topDistance - b.topDistance)

	const best = points[0]
	if (best) {
		const nextActiveSection = sectionNavGroupById[ids[best.key]] || 'home'
		if (activeSection.value !== nextActiveSection) {
			activeSection.value = nextActiveSection
		}
	}
}

function onHeaderScroll() {
	if (scrollTicking) return
	scrollTicking = true
	window.requestAnimationFrame(runScrollWork)
}

/* ---- Scroll-Spy für Auto-Highlight ---------------------------------- */
let sectionObserver: IntersectionObserver | null = null
let revealObserver: IntersectionObserver | null = null
let storyVideoObserver: IntersectionObserver | null = null
let motionSectionObserver: IntersectionObserver | null = null
let trustMarqueeObserver: IntersectionObserver | null = null
let landingObserverRefreshTimers: number[] = []
const storiesSectionEl = ref<HTMLElement | null>(null)
const heroSectionEl = ref<HTMLElement | null>(null)
const storyStationElements = new Map<string, HTMLElement>()
let scrollTicking = false
let reducedMotionQuery: MediaQueryList | null = null
let lastStoriesMetricsAt = 0
const STORIES_METRICS_INTERVAL_MS = 120
const STORIES_SPY_ANCHOR_VIEWPORT_RATIO = 0.36
const STORIES_SPY_TRIGGER_TOP_PX = 150
const STORIES_SPY_LAST_OFFSET_PX = 20
const HERO_HEADER_OFFSET_PX = 120
const HEADER_TOP_RESET_PX = 20
const HEADER_SCROLL_DELTA_TOGGLE_PX = 8
const MOTION_SECTION_ROOT_MARGIN = '280px 0px 280px 0px'
const STORY_VIDEO_THRESHOLD = 0.3
const STORY_VIDEO_ROOT_MARGIN = '140px 0px'
const SECTION_OBSERVER_STEPS = 21
const SECTION_OBSERVER_ROOT_MARGIN = '-84px 0px -55% 0px'
const REVEAL_FOLD_RATIO = 0.92
const REVEAL_THRESHOLD = 0.06
const REVEAL_ROOT_MARGIN = '120px 0px -6% 0px'
const TRUST_MARQUEE_THRESHOLD = 0.05
const TRUST_MARQUEE_ROOT_MARGIN = '120px 0px 120px 0px'

function cacheScrollElements() {
	heroSectionEl.value = document.getElementById(ids.home)
	storiesSectionEl.value = document.querySelector('.stories-neo') as HTMLElement | null
	storyStationElements.clear()
	storyStations.value.forEach((station) => {
		const el = document.getElementById(station.id)
		if (el) storyStationElements.set(station.id, el)
	})
}

function resetMotionVisibilityFlags() {
	isStoriesInView.value = false
}

function setupMotionSectionObserver() {
	motionSectionObserver?.disconnect()
	motionSectionObserver = null
	resetMotionVisibilityFlags()
	if (isNonLandingRoute.value || !motionEffectsEnabled.value) return

	const targets = [storiesSectionEl.value].filter((el): el is HTMLElement => Boolean(el))
	if (!targets.length) return

	motionSectionObserver = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			const target = entry.target as HTMLElement
			const isVisible = entry.isIntersecting
			if (target === storiesSectionEl.value) isStoriesInView.value = isVisible
		})
		}, { threshold: 0, rootMargin: MOTION_SECTION_ROOT_MARGIN })

	targets.forEach((el) => motionSectionObserver!.observe(el))
}

function updateReducedMotionPreference() {
	prefersReducedMotion.value = reducedMotionQuery?.matches ?? false
	if (prefersReducedMotion.value) {
		motionPaused.value = true
	}
	syncMotionMediaPlayback()
}

function setupStoryVideoObserver() {
	storyVideoObserver?.disconnect()
	storyVideoObserver = null
	if (isNonLandingRoute.value) return
	const videos = Array.from(document.querySelectorAll('.story-visual-video')) as HTMLVideoElement[]
	if (!videos.length) return
	storyVideoObserver = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			const video = entry.target as HTMLVideoElement
			video.muted = true
			video.defaultMuted = true
			video.playsInline = true
				if (entry.isIntersecting && !motionPaused.value) {
					void video.play().catch(() => {})
				} else {
					video.pause()
			}
		})
	}, { threshold: STORY_VIDEO_THRESHOLD, rootMargin: STORY_VIDEO_ROOT_MARGIN })
	videos.forEach((video) => storyVideoObserver!.observe(video))
}

function setupAutoplayVideoCompatibility() {
	if (isNonLandingRoute.value) return
	const videos = Array.from(document.querySelectorAll('video[autoplay]')) as HTMLVideoElement[]
	videos.forEach((video) => {
		video.muted = true
		video.defaultMuted = true
		video.playsInline = true
		if (!motionPaused.value) {
			void video.play().catch(() => {})
		}
	})
}

function setupSectionObserver() {
  sectionObserver?.disconnect()
  sectionObserver = null
  visibleRatios.clear()
  if (isNonLandingRoute.value) return

  // App-Bar Höhe einkalkulieren (ca. 80–88px)
  sectionObserver = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      const id = (e.target as HTMLElement).id
      visibleRatios.set(id, e.isIntersecting ? e.intersectionRatio : 0)
    })
    updateActiveSectionByPosition()
  }, {
    // feinere Schwellen → stabilere Ratios
    threshold: Array.from({ length: SECTION_OBSERVER_STEPS }, (_, i) => i / (SECTION_OBSERVER_STEPS - 1)),
    // oben etwas „wegschneiden“ wegen App-Bar, unten stark einkürzen
    rootMargin: SECTION_OBSERVER_ROOT_MARGIN,
  })

  // Nach DOM-Render observieren
  Object.values(ids).forEach((id) => {
    const el = document.getElementById(id)
    if (el) sectionObserver!.observe(el)
  })
}

function setupRevealObserver() {
	revealObserver?.disconnect()
	revealObserver = null
	if (isNonLandingRoute.value) return

	const targets = Array.from(document.querySelectorAll('[data-reveal]')) as HTMLElement[]
	if (!targets.length) return

	const foldThreshold = window.innerHeight * REVEAL_FOLD_RATIO
	targets.forEach((el) => {
		const rect = el.getBoundingClientRect()
		if (rect.top <= foldThreshold) {
			el.classList.add('reveal--in')
		}
	})

	revealObserver = new IntersectionObserver((entries) => {
		entries.forEach((e) => {
			if (e.isIntersecting) {
				(e.target as HTMLElement).classList.add('reveal--in')
				revealObserver?.unobserve(e.target)
			}
		})
	}, {
		threshold: REVEAL_THRESHOLD,
		rootMargin: REVEAL_ROOT_MARGIN,
	})

	targets.forEach((el) => {
		if (el.classList.contains('reveal--in')) return
		revealObserver!.observe(el)
	})
}

function setupTrustMarqueeObserver() {
	trustMarqueeObserver?.disconnect()
	trustMarqueeObserver = null
	isTrustMarqueeInView.value = true
	if (isNonLandingRoute.value) return
	const target = document.querySelector('.trust-marquee') as HTMLElement | null
	if (!target) return

	trustMarqueeObserver = new IntersectionObserver((entries) => {
		const entry = entries[0]
		isTrustMarqueeInView.value = Boolean(entry?.isIntersecting)
	}, {
		threshold: TRUST_MARQUEE_THRESHOLD,
		rootMargin: TRUST_MARQUEE_ROOT_MARGIN,
	})

	trustMarqueeObserver.observe(target)
}

function setupLandingObservers() {
	if (isNonLandingRoute.value) return
	setupSectionObserver()
	setupRevealObserver()
	setupStoryVideoObserver()
	setupMotionSectionObserver()
	setupTrustMarqueeObserver()
	runScrollWork()
}

function clearLandingObserverRefreshTimers() {
	landingObserverRefreshTimers.forEach((timer) => window.clearTimeout(timer))
	landingObserverRefreshTimers = []
}

function scheduleLandingObserverRefresh() {
	if (isNonLandingRoute.value) return
	clearLandingObserverRefreshTimers()

	const delays = [0, 180, 500, 1200]
	landingObserverRefreshTimers = delays.map((delay) => window.setTimeout(() => {
		cacheScrollElements()
		setupLandingObservers()
	}, delay))
}

onMounted(async () => {
	reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
	updateReducedMotionPreference()
	reducedMotionQuery.addEventListener('change', updateReducedMotionPreference)

	const savedLocale = localStorage.getItem('lf-locale')
	if (savedLocale === 'de' || savedLocale === 'en') {
		locale.value = savedLocale
	}
	await nextTick()
	setupAutoplayVideoCompatibility()
	cacheScrollElements()
	applySeoMeta()
	lastScrollY = window.scrollY || 0
	runScrollWork()
	window.addEventListener('scroll', onHeaderScroll, { passive: true })
	runWhenIdle(scheduleLandingObserverRefresh)
})

onBeforeUnmount(() => {
	clearLandingObserverRefreshTimers()
	sectionObserver?.disconnect()
	revealObserver?.disconnect()
	storyVideoObserver?.disconnect()
	motionSectionObserver?.disconnect()
	trustMarqueeObserver?.disconnect()
	reducedMotionQuery?.removeEventListener('change', updateReducedMotionPreference)
	window.removeEventListener('scroll', onHeaderScroll)
})
watch(() => isNonLandingRoute.value, async (isNonLanding) => {
  await nextTick()
  if (!isNonLanding) { setupAutoplayVideoCompatibility(); cacheScrollElements(); runWhenIdle(scheduleLandingObserverRefresh) }
  else { clearLandingObserverRefreshTimers(); sectionObserver?.disconnect(); revealObserver?.disconnect(); storyVideoObserver?.disconnect(); motionSectionObserver?.disconnect(); trustMarqueeObserver?.disconnect(); trustMarqueeObserver = null; isTrustMarqueeInView.value = true; resetMotionVisibilityFlags() }
})
watch(motionEffectsEnabled, async () => {
	await nextTick()
	runWhenIdle(scheduleLandingObserverRefresh)
})
watch(locale, async () => {
	await nextTick()
	applySeoMeta()
})
watch(() => route.fullPath, () => {
	applySeoMeta()
})
</script>

<style scoped src="./styles/app-shell.css"></style>
<style scoped>
</style>
